import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

interface Popup {
  id: number;
  title: string;
  body: string;
  actionUrl?: string | null;
  actionLabel?: string | null;
  isDismissable: boolean;
  authTarget: 'All' | 'LoggedIn' | 'LoggedOut';
  targetTier?: string | null;
}

interface Subscription {
  productTier: string | null;
  isActive: boolean;
}

const getBaseUrl = (): string => {
  return process.env.REACT_APP_DFS_MARKETING_API || '';
};

export default function PopupManager() {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const [popup, setPopup] = useState<Popup | null>(null);
  const [open, setOpen] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  useEffect(() => {
    // Fetch active popup for stokastic-tools
    const baseUrl = getBaseUrl();
    if (!baseUrl) return;
    if (isLoading) return;

    let cancelled = false;

    async function loadPopup() {
      try {
        // Delay before showing popup to avoid flash on page load
        await new Promise((resolve) => setTimeout(resolve, 2000));
        if (cancelled) return;

        const res = await fetch(`${baseUrl}/api/popups/active?app=stokastic-tools`);
        if (!res.ok) return;

        const contentType = res.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) return;

        const data: Popup | null = await res.json();
        if (!data || cancelled) return;

        // Check per-popup dismissal
        if (localStorage.getItem(`popup_dismissed_${data.id}`) === 'true') return;
        if (sessionStorage.getItem(`popup_session_${data.id}`) === 'true') return;

        // Client-side auth filtering
        if (data.authTarget === 'LoggedIn' && !isAuthenticated) return;
        if (data.authTarget === 'LoggedOut' && isAuthenticated) return;

        // Tier targeting check
        if (data.targetTier && isAuthenticated) {
          try {
            const token = await getAccessTokenSilently();
            const tierRes = await fetch(`${baseUrl}/api/subscriptions/my-tier`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (tierRes.ok) {
              const subs: Subscription[] = await tierRes.json();
              const hasTier = subs.some(
                (s) => s.isActive && s.productTier?.toLowerCase() === data.targetTier?.toLowerCase()
              );
              if (!hasTier) return;
            } else {
              // Cannot verify tier, skip showing popup
              return;
            }
          } catch {
            // Token retrieval or tier fetch failed, skip
            return;
          }
        } else if (data.targetTier && !isAuthenticated) {
          // Tier targeting requires auth, user is not authenticated
          return;
        }

        if (!cancelled) {
          setPopup(data);
          setOpen(true);
        }
      } catch {
        // Fetch failed silently
      }
    }

    loadPopup();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isLoading, getAccessTokenSilently]);

  const handleClose = () => {
    if (popup) {
      if (dontShowAgain) {
        localStorage.setItem(`popup_dismissed_${popup.id}`, 'true');
      }
      sessionStorage.setItem(`popup_session_${popup.id}`, 'true');
    }
    setOpen(false);
  };

  if (!open || !popup) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#1c252e',
          borderRadius: 3,
          border: '1px solid rgba(145, 158, 171, 0.16)',
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1, pr: 1 }}>
        <IconButton onClick={handleClose} sx={{ color: '#919eab' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 0, px: 4, pb: 2, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', mb: 2 }}>
          {popup.title}
        </Typography>
        <Typography sx={{ color: '#919eab', mb: 3, fontSize: 16, whiteSpace: 'pre-line' }}>
          {popup.body}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 4, pb: 3, flexDirection: 'column', gap: 1.5 }}>
        {popup.actionUrl && popup.actionLabel && (
          <Button
            variant="contained"
            size="large"
            fullWidth
            href={popup.actionUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClose}
            sx={{
              bgcolor: '#0e56f2',
              color: 'white',
              fontWeight: 700,
              py: 1.5,
              textTransform: 'none',
              '&:hover': { bgcolor: '#0a45c7' },
            }}
          >
            {popup.actionLabel}
          </Button>
        )}

        {popup.isDismissable && (
          <FormControlLabel
            control={
              <Checkbox
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                sx={{
                  color: '#637381',
                  '&.Mui-checked': { color: '#0e56f2' },
                }}
              />
            }
            label="Don't show this again"
            sx={{ color: '#637381', mr: 0 }}
          />
        )}
      </DialogActions>
    </Dialog>
  );
}
